import { useState, useMemo, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  Users, 
  Target, 
  TrendingUp, 
  Shield, 
  AlertTriangle,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  HelpCircle,
  AlertCircle,
  Clock,
  FileText,
  Zap,
  Loader2,
  MessageSquareWarning,
  RotateCcw,
  CheckCircle2
} from 'lucide-react'
import { clsx } from 'clsx'
import type { HypothesisDimension, HypothesisSummary, ConflictNotification } from '@/types'

// 关联证据类型（API 响应已通过拦截器转换为 camelCase）
interface LinkedEvidence {
  id: string
  sourceType: string
  contentSummary: string
  signalStrength: string
  occurredAt: string
  status: string
}

// 关联动作类型（API 响应已通过拦截器转换为 camelCase）
interface LinkedAction {
  id: string
  actionType: string
  content: string
  timeCostDisplay: string
  priority: string
  status: string
  warning?: string
}

interface HypothesisTreeProps {
  dimensions: Record<HypothesisDimension, HypothesisSummary[]>
  activeDimension?: HypothesisDimension
  onDimensionChange?: (dimension: HypothesisDimension) => void
  onHypothesisClick?: (hypothesis: HypothesisSummary) => void
  onConfirm?: (hypothesisId: string, reasoning?: string) => void
  onFalsify?: (hypothesisId: string, reasoning?: string) => void
  // 新增：获取关联数据的回调
  onFetchEvidences?: (hypothesisId: string) => Promise<LinkedEvidence[]>
  onFetchActions?: (hypothesisId: string) => Promise<{ A: LinkedAction[], B: LinkedAction[], C: LinkedAction[] }>
  // 冲突通知回调（TASK-H5）
  onFetchConflicts?: (hypothesisId: string) => Promise<ConflictNotification[]>
  onResolveConflict?: (conflictId: string, resolution: 'keep' | 'rollback_and_update', reasoning?: string) => Promise<void>
}

// 维度配置（支持小写值）
const DIMENSION_CONFIG: Record<string, {
  label: string
  description: string
  icon: React.ElementType
  color: string
  bgColor: string
}> = {
  customer: {
    label: '客户/结构',
    description: 'EB可达性、Coach有效性、TB态度',
    icon: Users,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
  },
  demand: {
    label: '需求',
    description: '需求层级、组织层闭环、痛点/痒点',
    icon: Target,
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
  },
  value: {
    label: '价值',
    description: 'ROI量化、决策三角形、价值口径',
    icon: TrendingUp,
    color: 'text-green-600',
    bgColor: 'bg-green-50',
  },
  barrier: {
    label: '壁垒',
    description: '评估标准、竞争排他性、技术门槛',
    icon: Shield,
    color: 'text-amber-600',
    bgColor: 'bg-amber-50',
  },
  risk: {
    label: '风险',
    description: '隐匿风险、变更检测、突发事件',
    icon: AlertTriangle,
    color: 'text-red-600',
    bgColor: 'bg-red-50',
  },
}

// 状态配置（支持小写值）
const STATUS_CONFIG: Record<string, {
  label: string
  icon: React.ElementType
  color: string
  bgColor: string
  showDiagnosis: boolean  // 是否显示诊断区块
}> = {
  unknown: {
    label: '假设：未知 (Unknown)',
    icon: HelpCircle,
    color: 'text-gray-500',
    bgColor: 'bg-gray-100',
    showDiagnosis: true,
  },
  plausible: {
    label: '假设：存疑 (Plausible)',
    icon: AlertCircle,
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
    showDiagnosis: true,
  },
  confirmed: {
    label: '已确认 (Confirmed)',
    icon: Check,
    color: 'text-green-600',
    bgColor: 'bg-green-100',
    showDiagnosis: false,
  },
  falsified: {
    label: '结论不成立 (Gap Confirmed)',
    icon: X,
    color: 'text-red-600',
    bgColor: 'bg-red-100',
    showDiagnosis: false,
  },
}

// 风险等级配置（TASK-H6）
const RISK_LEVEL_CONFIG: Record<string, {
  label: string
  color: string
  bgColor: string
  borderColor: string
  cardBg: string
}> = {
  fatal_gap: {
    label: '⚠️ 致命缺口',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200',
    cardBg: 'bg-red-50/50',
  },
  key_pending: {
    label: '🔶 关键待验证',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
    borderColor: 'border-orange-200',
    cardBg: 'bg-orange-50/50',
  },
  none: {
    label: '',
    color: '',
    bgColor: '',
    borderColor: '',
    cardBg: '',
  },
}

// 获取风险等级显示配置（TASK-H6）
// 优先使用 riskLevel，兼容兜底到 isFatalGap
const getRiskDisplay = (hypothesis: HypothesisSummary) => {
  // 优先使用 riskLevel
  if (hypothesis.riskLevel && hypothesis.riskLevel !== 'none') {
    return RISK_LEVEL_CONFIG[hypothesis.riskLevel] || null
  }
  // 兼容兜底：后端无 riskLevel 时使用 isFatalGap
  if (hypothesis.isFatalGap) {
    return RISK_LEVEL_CONFIG['fatal_gap']
  }
  return null
}

// 获取卡片边框样式（TASK-H6）
const getCardBorderStyle = (hypothesis: HypothesisSummary, isSelected: boolean) => {
  const risk = getRiskDisplay(hypothesis)
  if (risk?.borderColor) {
    return `${risk.borderColor} ${risk.cardBg}`
  }
  if (isSelected) {
    return 'border-primary-300 bg-primary-50/50'
  }
  return 'border-gray-100 bg-white hover:border-gray-200'
}

// 获取配置的辅助函数（支持大小写）
const getDimensionConfig = (dim: string) => DIMENSION_CONFIG[dim.toLowerCase()] || DIMENSION_CONFIG['customer']
const getStatusConfig = (status: string) => STATUS_CONFIG[status.toLowerCase()] || STATUS_CONFIG['unknown']

export default function HypothesisTree({
  dimensions,
  activeDimension = 'customer',
  onDimensionChange,
  onHypothesisClick,
  onConfirm,
  onFalsify,
  onFetchEvidences,
  onFetchActions,
  onFetchConflicts,
  onResolveConflict,
}: HypothesisTreeProps) {
  const [selectedHypothesis, setSelectedHypothesis] = useState<string | null>(null)
  const [expandedSection, setExpandedSection] = useState<'evidences' | 'actions' | 'conflicts' | null>(null)
  const [linkedEvidences, setLinkedEvidences] = useState<LinkedEvidence[]>([])
  const [linkedActions, setLinkedActions] = useState<{ A: LinkedAction[], B: LinkedAction[], C: LinkedAction[] } | null>(null)
  const [loadingEvidences, setLoadingEvidences] = useState(false)
  const [loadingActions, setLoadingActions] = useState(false)
  // 冲突通知状态（TASK-H5）
  const [conflicts, setConflicts] = useState<ConflictNotification[]>([])
  const [loadingConflicts, setLoadingConflicts] = useState(false)
  const [resolvingConflict, setResolvingConflict] = useState<string | null>(null)

  // 当选中假设变化时，重置展开状态
  useEffect(() => {
    setExpandedSection(null)
    setLinkedEvidences([])
    setLinkedActions(null)
    setConflicts([])
  }, [selectedHypothesis])

  // 加载冲突通知（TASK-H5）
  const handleLoadConflicts = async (hypothesisId: string) => {
    if (!onFetchConflicts) {
      setConflicts([])
      setExpandedSection('conflicts')
      return
    }
    setLoadingConflicts(true)
    try {
      const result = await onFetchConflicts(hypothesisId)
      setConflicts(result || [])
      setExpandedSection('conflicts')
    } catch (error) {
      console.error('Failed to load conflicts:', error)
      setConflicts([])
      setExpandedSection('conflicts')
    } finally {
      setLoadingConflicts(false)
    }
  }

  // 处理冲突（TASK-H5）
  const handleResolveConflict = async (
    conflictId: string, 
    resolution: 'keep' | 'rollback_and_update',
    hypothesisId: string
  ) => {
    if (!onResolveConflict) return
    setResolvingConflict(conflictId)
    try {
      await onResolveConflict(conflictId, resolution)
      // 刷新冲突列表
      if (onFetchConflicts) {
        const result = await onFetchConflicts(hypothesisId)
        setConflicts(result || [])
      }
    } catch (error) {
      console.error('Failed to resolve conflict:', error)
    } finally {
      setResolvingConflict(null)
    }
  }

  // 加载关联证据
  const handleLoadEvidences = async (hypothesisId: string) => {
    if (!onFetchEvidences) {
      // 没有回调时也显示空状态
      setLinkedEvidences([])
      setExpandedSection('evidences')
      return
    }
    setLoadingEvidences(true)
    try {
      const evidences = await onFetchEvidences(hypothesisId)
      setLinkedEvidences(evidences || [])
      setExpandedSection('evidences')
    } catch (error) {
      console.error('Failed to load evidences:', error)
      // 失败时也显示空状态，而非无反馈
      setLinkedEvidences([])
      setExpandedSection('evidences')
    } finally {
      setLoadingEvidences(false)
    }
  }

  // 加载关联动作
  const handleLoadActions = async (hypothesisId: string) => {
    if (!onFetchActions) {
      // 没有回调时也显示空状态
      setLinkedActions({ A: [], B: [], C: [] })
      setExpandedSection('actions')
      return
    }
    setLoadingActions(true)
    try {
      const actions = await onFetchActions(hypothesisId)
      setLinkedActions(actions || { A: [], B: [], C: [] })
      setExpandedSection('actions')
    } catch (error) {
      console.error('Failed to load actions:', error)
      // 失败时也显示空状态，而非无反馈
      setLinkedActions({ A: [], B: [], C: [] })
      setExpandedSection('actions')
    } finally {
      setLoadingActions(false)
    }
  }

  // 计算每个维度的统计（TASK-H6: 支持 riskLevel，兼容 isFatalGap）
  const dimensionStats = useMemo(() => {
    const stats: Record<string, { total: number; fatalGaps: number; keyPending: number; confirmed: number }> = {
      customer: { total: 0, fatalGaps: 0, keyPending: 0, confirmed: 0 },
      demand: { total: 0, fatalGaps: 0, keyPending: 0, confirmed: 0 },
      value: { total: 0, fatalGaps: 0, keyPending: 0, confirmed: 0 },
      barrier: { total: 0, fatalGaps: 0, keyPending: 0, confirmed: 0 },
      risk: { total: 0, fatalGaps: 0, keyPending: 0, confirmed: 0 },
    }

    Object.entries(dimensions).forEach(([dim, hypotheses]) => {
      const d = dim.toLowerCase()
      if (stats[d]) {
        stats[d].total = hypotheses.length
        // 优先使用 riskLevel，fallback 到 isFatalGap
        stats[d].fatalGaps = hypotheses.filter(h => 
          h.riskLevel === 'fatal_gap' || (!h.riskLevel && h.isFatalGap)
        ).length
        stats[d].keyPending = hypotheses.filter(h => h.riskLevel === 'key_pending').length
        stats[d].confirmed = hypotheses.filter(h => h.status?.toLowerCase() === 'confirmed').length
      }
    })

    return stats
  }, [dimensions])

  // 获取活动维度的假设列表 - 使用Object.entries确保正确获取
  const activeHypotheses = (() => {
    const dimKey = activeDimension.toLowerCase()
    // 遍历dimensions对象找到匹配的维度
    for (const [key, value] of Object.entries(dimensions)) {
      if (key.toLowerCase() === dimKey) {
        return value as HypothesisSummary[]
      }
    }
    return []
  })()
  
  const activeConfig = getDimensionConfig(activeDimension)

  return (
    <div className="flex gap-6">
      {/* 左侧：维度导航 */}
      <div className="w-64 flex-shrink-0">
        <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">
          五步法维度
        </h3>
        <div className="space-y-2">
          {(Object.keys(DIMENSION_CONFIG) as HypothesisDimension[]).map((dim) => {
            const config = getDimensionConfig(dim)
            const stats = dimensionStats[dim.toLowerCase()]
            const Icon = config.icon
            const isActive = activeDimension === dim

            return (
              <button
                key={dim}
                onClick={() => onDimensionChange?.(dim)}
                className={clsx(
                  'w-full flex items-center justify-between p-3 rounded-xl transition-all',
                  isActive
                    ? `${config.bgColor} ${config.color}`
                    : 'hover:bg-gray-50 text-gray-600'
                )}
              >
                <div className="flex items-center gap-3">
                  <div className={clsx(
                    'p-2 rounded-lg',
                    isActive ? 'bg-white/60' : 'bg-gray-100'
                  )}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="text-left">
                    <div className="font-medium">{config.label}</div>
                    <div className="text-xs opacity-70">{stats.total} 个假设</div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* 致命缺口计数（红色）*/}
                  {stats.fatalGaps > 0 && (
                    <span 
                      className="w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs rounded-full font-medium"
                      title={`${stats.fatalGaps} 个致命缺口`}
                    >
                      {stats.fatalGaps}
                    </span>
                  )}
                  {/* 关键待验证计数（橙色）*/}
                  {stats.keyPending > 0 && (
                    <span 
                      className="w-5 h-5 flex items-center justify-center bg-orange-500 text-white text-xs rounded-full font-medium"
                      title={`${stats.keyPending} 个关键待验证`}
                    >
                      {stats.keyPending}
                    </span>
                  )}
                  <ChevronRight className={clsx(
                    'w-4 h-4 transition-transform',
                    isActive && 'rotate-90'
                  )} />
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* 右侧：假设列表 */}
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-4">
          <div className={clsx('p-2 rounded-lg', activeConfig.bgColor)}>
            <activeConfig.icon className={clsx('w-5 h-5', activeConfig.color)} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{activeConfig.label}</h3>
            <p className="text-sm text-gray-500">{activeConfig.description}</p>
          </div>
        </div>

        <div key={`hypothesis-list-${activeDimension}-${activeHypotheses.length}`} className="space-y-3">
            {activeHypotheses.length > 0 ? (
              activeHypotheses.map((hypothesis) => {
                const statusConfig = getStatusConfig(hypothesis.status)
                const StatusIcon = statusConfig.icon
                const isSelected = selectedHypothesis === hypothesis.id

                // 获取风险显示配置（TASK-H6）
                const riskDisplay = getRiskDisplay(hypothesis)

                return (
                  <motion.div
                    key={`${activeDimension}-${hypothesis.id}`}
                    layout
                    className={clsx(
                      'p-4 rounded-xl border-2 transition-all cursor-pointer',
                      getCardBorderStyle(hypothesis, isSelected)
                    )}
                    onClick={() => {
                      setSelectedHypothesis(isSelected ? null : hypothesis.id)
                      onHypothesisClick?.(hypothesis)
                    }}
                  >
                    {/* 第一行：假设陈述（正向表达标题） + 证据数量 */}
                    <div className="flex items-start justify-between mb-2">
                      <p className="text-gray-800 font-medium">
                        {hypothesis.statement}
                      </p>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                        {hypothesis.evidenceCount} 条证据
                      </span>
                    </div>

                    {/* 第二行：状态标签 + 致命缺口标记 + 待复核徽章 */}
                    <div className="flex items-center gap-2 mb-2">
                      <span className={clsx(
                        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
                        statusConfig.bgColor,
                        statusConfig.color
                      )}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </span>
                      {/* 风险等级标签（TASK-H6）- 使用 riskLevel 优先，fallback 到 isFatalGap */}
                      {riskDisplay && riskDisplay.label && (
                        <span className={clsx(
                          'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                          riskDisplay.bgColor,
                          riskDisplay.color
                        )}>
                          {riskDisplay.label}
                        </span>
                      )}
                      {/* 待复核徽章（TASK-H5）*/}
                      {hypothesis.hasUnresolvedConflicts && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedHypothesis(hypothesis.id)
                            handleLoadConflicts(hypothesis.id)
                          }}
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors"
                          title="有证据冲突需要人工复核"
                        >
                          <MessageSquareWarning className="w-3 h-3" />
                          待复核{hypothesis.unresolvedConflictCount ? ` (${hypothesis.unresolvedConflictCount})` : ''}
                        </button>
                      )}
                    </div>

                    {/* 诊断区块 - 仅在 unknown/plausible 状态时显示 */}
                    {statusConfig.showDiagnosis && hypothesis.reasoning && (
                      <div className="mb-3 p-3 bg-amber-50 border border-amber-100 rounded-lg">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold text-amber-700">诊断</span>
                          <span className="text-gray-500">：</span>
                          {hypothesis.reasoning}
                        </p>
                      </div>
                    )}

                    {/* 置信度条 - 带tooltip解释 */}
                    <div 
                      className="flex items-center gap-2 mb-2 group relative"
                      title={`确信度 ${Math.round(hypothesis.confidence * 100)}%：表示该假设的证据支持程度。\n• 高确信度 + 待验证 = 有证据支持但尚未人工确认\n• 致命缺口表示该假设对赢单至关重要`}
                    >
                      <div className="flex-1 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={clsx(
                            'h-full rounded-full transition-all',
                            hypothesis.confidence >= 0.7 ? 'bg-green-500' :
                            hypothesis.confidence >= 0.4 ? 'bg-amber-500' :
                            'bg-gray-300'
                          )}
                          style={{ width: `${hypothesis.confidence * 100}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-500 cursor-help">
                        {Math.round(hypothesis.confidence * 100)}%
                      </span>
                    </div>

                    {/* 更新时间（诊断模式下隐藏reasoning，避免重复显示） */}
                    {hypothesis.updatedAt && (
                      <div className="flex items-center gap-4 text-xs text-gray-400">
                        {/* 仅在非诊断模式下显示reasoning */}
                        {!statusConfig.showDiagnosis && hypothesis.reasoning && (
                          <span className="truncate flex-1">{hypothesis.reasoning}</span>
                        )}
                        <span className="flex items-center gap-1 flex-shrink-0">
                          <Clock className="w-3 h-3" />
                          {new Date(hypothesis.updatedAt).toLocaleDateString()}
                        </span>
                      </div>
                    )}

                    {/* 展开的操作区域 */}
                    <AnimatePresence>
                      {isSelected && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="mt-4 pt-3 border-t border-gray-100"
                        >
                          {/* 关联数据快捷按钮 */}
                          <div className="flex items-center gap-2 mb-3">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (expandedSection === 'evidences') {
                                  setExpandedSection(null)
                                } else {
                                  handleLoadEvidences(hypothesis.id)
                                }
                              }}
                              disabled={loadingEvidences}
                              className={clsx(
                                'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                                expandedSection === 'evidences'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              )}
                            >
                              {loadingEvidences ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <FileText className="w-3 h-3" />
                              )}
                              关联证据 ({hypothesis.evidenceCount})
                              <ChevronDown className={clsx(
                                'w-3 h-3 transition-transform',
                                expandedSection === 'evidences' && 'rotate-180'
                              )} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                if (expandedSection === 'actions') {
                                  setExpandedSection(null)
                                } else {
                                  handleLoadActions(hypothesis.id)
                                }
                              }}
                              disabled={loadingActions}
                              className={clsx(
                                'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                                expandedSection === 'actions'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              )}
                            >
                              {loadingActions ? (
                                <Loader2 className="w-3 h-3 animate-spin" />
                              ) : (
                                <Zap className="w-3 h-3" />
                              )}
                              推荐动作
                              <ChevronDown className={clsx(
                                'w-3 h-3 transition-transform',
                                expandedSection === 'actions' && 'rotate-180'
                              )} />
                            </button>
                            {/* 冲突通知按钮（TASK-H5）- 仅终态时显示 */}
                            {(hypothesis.status === 'confirmed' || hypothesis.status === 'falsified') && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  if (expandedSection === 'conflicts') {
                                    setExpandedSection(null)
                                  } else {
                                    handleLoadConflicts(hypothesis.id)
                                  }
                                }}
                                disabled={loadingConflicts}
                                className={clsx(
                                  'flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors',
                                  expandedSection === 'conflicts'
                                    ? 'bg-orange-100 text-orange-700'
                                    : hypothesis.hasUnresolvedConflicts
                                    ? 'bg-orange-50 text-orange-600 hover:bg-orange-100'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                )}
                              >
                                {loadingConflicts ? (
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                ) : (
                                  <MessageSquareWarning className="w-3 h-3" />
                                )}
                                冲突记录
                                {hypothesis.unresolvedConflictCount ? (
                                  <span className="ml-1 px-1.5 py-0.5 bg-orange-200 text-orange-800 rounded-full text-[10px]">
                                    {hypothesis.unresolvedConflictCount}
                                  </span>
                                ) : null}
                                <ChevronDown className={clsx(
                                  'w-3 h-3 transition-transform',
                                  expandedSection === 'conflicts' && 'rotate-180'
                                )} />
                              </button>
                            )}
                          </div>

                          {/* 关联证据列表 */}
                          <AnimatePresence>
                            {expandedSection === 'evidences' && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mb-3 space-y-2"
                              >
                                {linkedEvidences.length > 0 ? (
                                  linkedEvidences.map((evidence) => (
                                  <div
                                    key={evidence.id}
                                    className="p-2 bg-blue-50 rounded-lg text-sm"
                                  >
                                    <div className="flex items-center justify-between mb-1">
                                      <span className={clsx(
                                        'px-1.5 py-0.5 rounded text-xs font-medium',
                                          evidence.signalStrength === 'strong' ? 'bg-green-100 text-green-700' :
                                          evidence.signalStrength === 'medium' ? 'bg-amber-100 text-amber-700' :
                                        'bg-gray-100 text-gray-600'
                                      )}>
                                          {evidence.signalStrength === 'strong' ? '强信号' :
                                           evidence.signalStrength === 'medium' ? '中信号' : '弱信号'}
                                      </span>
                                      <span className="text-xs text-gray-400">
                                          {evidence.occurredAt ? new Date(evidence.occurredAt).toLocaleDateString() : ''}
                                      </span>
                                    </div>
                                    <p className="text-gray-700 line-clamp-2">
                                        {evidence.contentSummary || evidence.sourceType || '暂无摘要信息'}
                                    </p>
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                                    <p className="text-sm text-gray-500">暂无关联证据</p>
                                    <p className="text-xs text-gray-400 mt-1">添加新证据后会自动关联</p>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* 关联动作列表 */}
                          <AnimatePresence>
                            {expandedSection === 'actions' && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mb-3 space-y-2"
                              >
                                {(() => {
                                  const allActions = linkedActions 
                                    ? [...(linkedActions.A || []), ...(linkedActions.B || []), ...(linkedActions.C || [])]
                                    : []
                                  
                                  if (allActions.length === 0) {
                                    return (
                                      <div className="p-4 bg-amber-50 rounded-lg text-center">
                                        <Zap className="w-6 h-6 text-amber-400 mx-auto mb-2" />
                                        <p className="text-sm text-amber-700">暂无推荐动作</p>
                                        <p className="text-xs text-amber-500 mt-1">系统正在分析中，稍后自动生成</p>
                                      </div>
                                    )
                                  }
                                  
                                  return ['B', 'A', 'C'].map((grade) => {
                                    const actions = linkedActions?.[grade as 'A' | 'B' | 'C'] || []
                                  if (actions.length === 0) return null
                                  return actions.slice(0, 2).map((action) => (
                                    <div
                                      key={action.id}
                                      className={clsx(
                                        'p-2 rounded-lg text-sm',
                                        grade === 'A' ? 'bg-green-50' :
                                        grade === 'B' ? 'bg-blue-50' :
                                        'bg-amber-50'
                                      )}
                                    >
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className={clsx(
                                          'px-1.5 py-0.5 rounded text-xs font-medium',
                                          grade === 'A' ? 'bg-green-100 text-green-700' :
                                          grade === 'B' ? 'bg-blue-100 text-blue-700' :
                                          'bg-amber-100 text-amber-700'
                                        )}>
                                          {grade}级
                                        </span>
                                        <span className="text-xs text-gray-500">
                                            {action.timeCostDisplay}
                                        </span>
                                      </div>
                                      <p className="text-gray-700 line-clamp-2">
                                        {action.content}
                                      </p>
                                      {action.warning && (
                                        <p className="text-xs text-amber-600 mt-1">
                                          {action.warning}
                                        </p>
                                      )}
                                    </div>
                                  ))
                                  })
                                })()}
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* 冲突通知列表（TASK-H5）*/}
                          <AnimatePresence>
                            {expandedSection === 'conflicts' && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="mb-3 space-y-2"
                              >
                                {conflicts.length > 0 ? (
                                  conflicts.map((conflict) => (
                                    <div
                                      key={conflict.id}
                                      className={clsx(
                                        'p-3 rounded-lg text-sm border',
                                        conflict.resolved
                                          ? 'bg-gray-50 border-gray-200'
                                          : 'bg-orange-50 border-orange-200'
                                      )}
                                    >
                                      <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <span className={clsx(
                                            'px-1.5 py-0.5 rounded text-xs font-medium',
                                            conflict.source === 'llm_diagnosis'
                                              ? 'bg-purple-100 text-purple-700'
                                              : 'bg-blue-100 text-blue-700'
                                          )}>
                                            {conflict.source === 'llm_diagnosis' ? 'LLM诊断' : '信号观测'}
                                          </span>
                                          {conflict.resolved && (
                                            <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                              已处理
                                            </span>
                                          )}
                                        </div>
                                        <span className="text-xs text-gray-400">
                                          {new Date(conflict.createdAt).toLocaleDateString()}
                                        </span>
                                      </div>
                                      
                                      <p className="text-gray-700 mb-2">{conflict.reason}</p>
                                      
                                      <div className="text-xs text-gray-500 mb-2">
                                        <span className="font-medium">当前状态:</span> {conflict.currentStatus}
                                        <span className="mx-2">→</span>
                                        <span className="font-medium">建议:</span> {conflict.suggestedStatus}
                                      </div>
                                      
                                      {!conflict.resolved && onResolveConflict && (
                                        <div className="flex items-center gap-2 mt-2 pt-2 border-t border-orange-100">
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleResolveConflict(conflict.id, 'keep', hypothesis.id)
                                            }}
                                            disabled={resolvingConflict === conflict.id}
                                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-100 text-gray-700 rounded text-xs font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
                                          >
                                            {resolvingConflict === conflict.id ? (
                                              <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <CheckCircle2 className="w-3 h-3" />
                                            )}
                                            维持原状态
                                          </button>
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              handleResolveConflict(conflict.id, 'rollback_and_update', hypothesis.id)
                                            }}
                                            disabled={resolvingConflict === conflict.id}
                                            className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-orange-100 text-orange-700 rounded text-xs font-medium hover:bg-orange-200 transition-colors disabled:opacity-50"
                                          >
                                            {resolvingConflict === conflict.id ? (
                                              <Loader2 className="w-3 h-3 animate-spin" />
                                            ) : (
                                              <RotateCcw className="w-3 h-3" />
                                            )}
                                            采纳建议
                                          </button>
                                        </div>
                                      )}
                                      
                                      {conflict.resolved && conflict.resolution && (
                                        <div className="text-xs text-gray-500 mt-1">
                                          处理方式: {conflict.resolution === 'keep' ? '维持原状态' : '采纳建议'}
                                        </div>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                                    <MessageSquareWarning className="w-6 h-6 text-gray-400 mx-auto mb-2" />
                                    <p className="text-sm text-gray-500">暂无冲突记录</p>
                                    <p className="text-xs text-gray-400 mt-1">当自动诊断与终态冲突时会产生记录</p>
                                  </div>
                                )}
                              </motion.div>
                            )}
                          </AnimatePresence>

                          {/* 确认/证伪按钮 */}
                          {hypothesis.status !== 'confirmed' && hypothesis.status !== 'falsified' && (
                            <div className="flex items-center gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              onConfirm?.(hypothesis.id)
                            }}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-green-100 text-green-700 rounded-lg text-sm font-medium hover:bg-green-200 transition-colors"
                          >
                            <Check className="w-4 h-4" />
                            确认
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              onFalsify?.(hypothesis.id)
                            }}
                            className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                          >
                            <X className="w-4 h-4" />
                            证伪
                          </button>
                            </div>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                )
              })
            ) : (
              <div className="text-center py-12 text-gray-400">
                <HelpCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>该维度暂无假设</p>
                <p className="text-sm mt-1">添加证据后系统将自动生成</p>
              </div>
            )}
        </div>
      </div>
    </div>
  )
}

/**
 * 假设树骨架屏
 */
export function HypothesisTreeSkeleton() {
  return (
    <div className="flex gap-6 animate-pulse">
      <div className="w-64 flex-shrink-0 space-y-2">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-16 bg-gray-100 rounded-xl" />
        ))}
      </div>
      <div className="flex-1 space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-100 rounded-xl" />
        ))}
      </div>
    </div>
  )
}

